import { ReactNode } from 'react';
import { Dialog, DialogProps, Heading, Modal, ModalOverlay } from 'react-aria-components';
import { cx } from '../util';
import { ClarionButton } from './ClarionButton';

export type ClarionDrawerProps = DialogProps & {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  children: ReactNode;
  isDismissable?: boolean;
  showCloseButton?: boolean;
  side?: 'left' | 'right';
};

export function ClarionDrawer({
  className,
  children,
  title,
  isOpen,
  onClose,
  isDismissable = true,
  style,
  showCloseButton = true,
  side = 'right',
  ...props
}: ClarionDrawerProps) {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => !newIsOpen && onClose?.()}
      isDismissable={isDismissable}
      className={cx(
        'fixed inset-0 z-20 bg-black/[35%] grid place-items-center',
        'transition-opacity duration-300 ease-in-out',
        isOpen ? 'opacity-100 motion-safe:animate-fade-in' : 'opacity-0',
      )}
    >
      <Modal
        className={cx(
          'fixed inset-y-0 w-full max-w-md bg-white dark:bg-neutral-800 shadow-2xl',
          'transform transition-transform duration-300 ease-in-out',
          side === 'left'
            ? [
                'left-0 border-r border-black/10 dark:border-white/15',
                isOpen ? 'translate-x-0 motion-safe:animate-slide-in-left' : '-translate-x-full',
              ]
            : [
                'right-0 border-l border-black/10 dark:border-white/15',
                isOpen ? 'translate-x-0 motion-safe:animate-slide-in-right' : 'translate-x-full',
              ],
          className,
        )}
        style={style}
      >
        <Dialog {...props} className={cx('h-full flex flex-col outline-hidden', className)}>
          {isOpen && (
            <>
              <div className="flex justify-between items-center px-4 py-2 border-b border-black/10 dark:border-white/15">
                {title && (
                  <Heading slot="title" className="text-xl font-semibold">
                    {title}
                  </Heading>
                )}
                {showCloseButton && (
                  <ClarionButton
                    icon="cross"
                    variant="minimal"
                    size="large"
                    onClick={onClose}
                    className="-mr-2"
                    aria-label="Close"
                  />
                )}
              </div>
              <div className="flex-1 overflow-auto">{children}</div>
            </>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
